import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardActionArea
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import BookIcon from '@mui/icons-material/Book';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';

// Laboran Dashboard Home Component
const LaboranDashboardHome = () => {
  const [userName, setUserName] = useState('Laboran');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        try {
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            setUserName(userDoc.data().nama || user.email || 'Laboran');
          } else {
            setUserName(user.email || 'Laboran');
          }
        } catch (error) {
          console.error("Error fetching user name:", error);
          setUserName(user.email || 'Laboran');
        }
      } else {
        setUserName('Laboran');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Define shortcut cards - Priority approvals first
  const shortcutCards = [
    // High Priority - Approvals
    {
      title: 'Persetujuan Alat',
      description: 'Tinjau dan setujui permintaan peminjaman alat',
      icon: <AssignmentTurnedInIcon />,
      path: 'persetujuan-alat-laboran',
      color: '#d32f2f',
      priority: 'high',
      category: 'approval'
    },
    {
      title: 'Persetujuan Bahan',
      description: 'Tinjau dan setujui permintaan bahan',
      icon: <AssignmentTurnedInIcon />,
      path: 'persetujuan-bahan-laboran',
      color: '#d32f2f',
      priority: 'high',
      category: 'approval'
    },
    // Medium Priority - Monitoring
    {
      title: 'Monitoring Alat',
      description: 'Pantau status dan penggunaan alat',
      icon: <MonitorHeartIcon />,
      path: 'monitoring-alat',
      color: '#1976d2',
      priority: 'medium',
      category: 'monitoring'
    },
    {
      title: 'Monitoring Bahan',
      description: 'Pantau stok dan penggunaan bahan',
      icon: <MonitorHeartIcon />,
      path: 'monitoring-bahan',
      color: '#1976d2',
      priority: 'medium',
      category: 'monitoring'
    },
    // Low Priority - Other
    {
      title: 'Logbook',
      description: 'Lihat dan kelola logbook penggunaan',
      icon: <BookIcon />,
      path: 'logbook-laboran',
      color: '#2e7d32',
      priority: 'low',
      category: 'other'
    },
    {
      title: 'Jadwal Penggunaan',
      description: 'Lihat jadwal penggunaan alat',
      icon: <CalendarTodayIcon />,
      path: 'jadwal-penggunaan-laboran',
      color: '#7b1fa2',
      priority: 'low',
      category: 'other'
    },
    {
      title: 'Scan Alat',
      description: 'Scan QR code untuk menggunakan alat',
      icon: <QrCodeScannerIcon />,
      path: '/scan-alat',
      color: '#1976d2',
      priority: 'low',
      category: 'other'
    },
  ];

  const handleCardClick = (path) => {
    if (path.startsWith('/')) {
      navigate(path);
    } else {
      navigate(path);
    }
  };

  // Group cards by category
  const approvalCards = shortcutCards.filter(card => card.category === 'approval');
  const monitoringCards = shortcutCards.filter(card => card.category === 'monitoring');
  const otherCards = shortcutCards.filter(card => card.category === 'other');

  return (
    <Box sx={{ 
      p: { xs: 1.5, sm: 2, md: 3 },
      maxWidth: '1400px',
      mx: 'auto'
    }}>
      {/* Welcome Section with Logo */}
      <Box 
        sx={{ 
          mb: { xs: 2, sm: 3 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: 2
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
              mb: 0.5
            }}
          >
            Selamat datang, {loading ? '...' : userName}!
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            Dashboard Laboran - Sistem Manajemen Laboratorium Rekayasa Kosmetik
          </Typography>
        </Box>
        
        {/* Logo - Top Right */}
        <Box 
          sx={{ 
            display: 'block',
            flexShrink: 0
          }}
        >
          <Box
            component="img"
            src="/images/LOGO PRODI REKOS.jpg"
            alt="Logo Prodi Rekayasa Kosmetik"
            sx={{
              maxWidth: { xs: '80px', sm: '120px' },
              height: 'auto',
              maxHeight: { xs: 50, sm: 80 },
              borderRadius: 1,
              display: 'block'
            }}
          />
        </Box>
      </Box>

      {/* Priority Approvals Section */}
      {approvalCards.length > 0 && (
        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              mb: { xs: 2, sm: 2.5 },
              color: 'error.main'
            }}
          >
            ⚠️ Persetujuan Prioritas
          </Typography>
          
          <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
            {approvalCards.map((card, index) => (
              <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex' }}>
                <Card 
                  elevation={0}
                  sx={{ 
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    border: `3px solid ${card.color}`,
                    backgroundColor: `${card.color}08`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: 8,
                      borderColor: card.color,
                      backgroundColor: `${card.color}15`,
                      '& .card-icon': {
                        transform: 'scale(1.15)'
                      }
                    }
                  }}
                >
                  <CardActionArea 
                    onClick={() => handleCardClick(card.path)}
                    sx={{ 
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      p: { xs: 1.5, sm: 2 },
                      minHeight: { xs: 140, sm: 160, md: 170 },
                      width: '100%',
                      '&:hover': {
                        backgroundColor: 'transparent'
                      }
                    }}
                  >
                    <Box 
                      className="card-icon"
                      sx={{ 
                        color: card.color,
                        mb: { xs: 1, sm: 1.5 },
                        transition: 'transform 0.3s ease',
                        fontSize: { xs: 40, sm: 48, md: 52 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {card.icon}
                    </Box>
                    <CardContent 
                      sx={{ 
                        textAlign: 'center', 
                        p: 0,
                        pt: 0,
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        width: '100%',
                        minWidth: 0
                      }}
                    >
                      <Typography 
                        variant="subtitle1" 
                        component="div" 
                        sx={{ 
                          fontWeight: 700,
                          fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.0625rem' },
                          mb: { xs: 0.5, sm: 0.75 },
                          lineHeight: 1.3,
                          color: card.color,
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word'
                        }}
                      >
                        {card.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                          display: { xs: 'none', sm: 'block' },
                          lineHeight: 1.4,
                          px: 0.5,
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word'
                        }}
                      >
                        {card.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Monitoring Section */}
      {monitoringCards.length > 0 && (
        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              mb: { xs: 2, sm: 2.5 },
              color: 'text.primary'
            }}
          >
            Monitoring
          </Typography>
          
          <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
            {monitoringCards.map((card, index) => (
              <Grid item xs={6} sm={4} md={3} key={index} sx={{ display: 'flex' }}>
                <Card 
                  elevation={0}
                  sx={{ 
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    backgroundColor: 'background.paper',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: 6,
                      borderColor: card.color,
                      '& .card-icon': {
                        transform: 'scale(1.1)'
                      }
                    }
                  }}
                >
                  <CardActionArea 
                    onClick={() => handleCardClick(card.path)}
                    sx={{ 
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      p: { xs: 1.5, sm: 2 },
                      minHeight: { xs: 130, sm: 150, md: 160 },
                      width: '100%',
                      '&:hover': {
                        backgroundColor: 'transparent'
                      }
                    }}
                  >
                    <Box 
                      className="card-icon"
                      sx={{ 
                        color: card.color,
                        mb: { xs: 1, sm: 1.5 },
                        transition: 'transform 0.3s ease',
                        fontSize: { xs: 36, sm: 40, md: 44 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {card.icon}
                    </Box>
                    <CardContent 
                      sx={{ 
                        textAlign: 'center', 
                        p: 0,
                        pt: 0,
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        width: '100%',
                        minWidth: 0
                      }}
                    >
                      <Typography 
                        variant="subtitle1" 
                        component="div" 
                        sx={{ 
                          fontWeight: 600,
                          fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                          mb: { xs: 0.5, sm: 0.75 },
                          lineHeight: 1.3,
                          color: 'text.primary',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word'
                        }}
                      >
                        {card.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                          display: { xs: 'none', sm: 'block' },
                          lineHeight: 1.4,
                          px: 0.5,
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word'
                        }}
                      >
                        {card.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Other Features Section */}
      {otherCards.length > 0 && (
        <Box>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              mb: { xs: 2, sm: 2.5 },
              color: 'text.primary'
            }}
          >
            Menu Cepat
          </Typography>
          
          <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
            {otherCards.map((card, index) => (
              <Grid item xs={6} sm={4} md={3} key={index} sx={{ display: 'flex' }}>
                <Card 
                  elevation={0}
                  sx={{ 
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    backgroundColor: 'background.paper',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: 6,
                      borderColor: card.color,
                      '& .card-icon': {
                        transform: 'scale(1.1)'
                      }
                    }
                  }}
                >
                  <CardActionArea 
                    onClick={() => handleCardClick(card.path)}
                    sx={{ 
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      p: { xs: 1.5, sm: 2 },
                      minHeight: { xs: 130, sm: 150, md: 160 },
                      width: '100%',
                      '&:hover': {
                        backgroundColor: 'transparent'
                      }
                    }}
                  >
                    <Box 
                      className="card-icon"
                      sx={{ 
                        color: card.color,
                        mb: { xs: 1, sm: 1.5 },
                        transition: 'transform 0.3s ease',
                        fontSize: { xs: 36, sm: 40, md: 44 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {card.icon}
                    </Box>
                    <CardContent 
                      sx={{ 
                        textAlign: 'center', 
                        p: 0,
                        pt: 0,
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        width: '100%',
                        minWidth: 0
                      }}
                    >
                      <Typography 
                        variant="subtitle1" 
                        component="div" 
                        sx={{ 
                          fontWeight: 600,
                          fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                          mb: { xs: 0.5, sm: 0.75 },
                          lineHeight: 1.3,
                          color: 'text.primary',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word'
                        }}
                      >
                        {card.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                          display: { xs: 'none', sm: 'block' },
                          lineHeight: 1.4,
                          px: 0.5,
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word'
                        }}
                      >
                        {card.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default LaboranDashboardHome;

